import { LocalNotifications } from '@capacitor/local-notifications';

import { isStoreBuild } from './platform';

/**
 * Локальные напоминания.
 *
 * ЗАЧЕМ ИМЕННО ЛОКАЛЬНЫЕ. Они не требуют ни Firebase, ни ключей, ни серверного
 * кода: приложение планирует их само на устройстве. Для правила Apple о
 * минимальной функциональности они засчитываются наравне с серверными — системе
 * всё равно, кто уведомление поставил.
 *
 * ⚠️ В первую сборку НЕ добавляем плагин серверных пушей и не объявляем
 * соответствующую возможность в проекте. Если объявить её и не иметь рабочего
 * сервера, проверяющий увидит запрос разрешения, ничего не дождётся — и это
 * повод для отказа. Серверные пуши выпускаем обновлением после одобрения.
 *
 * ⚠️ ПРЕДЕЛ iOS — 64 запланированных уведомления одновременно. Поэтому
 * расписание планируется скользящим окном на ближайшую неделю, а не целиком:
 * иначе часть просто потеряется, причём молча.
 */

const DAILY_ID = 1; // ежедневное напоминание — всегда одно и то же место
const LESSON_BASE = 1000; // занятия
const HOMEWORK_BASE = 2000; // сроки домашних работ
const MAX_SCHEDULED = 60; // с запасом до предела iOS

const PREF_KEY = 'onebilim:reminders';

export type ReminderPrefs = {
  /** Ежедневное напоминание об учёбе. */
  daily: boolean;
  /** Время в формате «19:00». */
  dailyTime: string;
  /** За час до занятия. */
  lessons: boolean;
  /** Накануне срока сдачи. */
  homework: boolean;
};

const DEFAULTS: ReminderPrefs = { daily: false, dailyTime: '19:00', lessons: true, homework: true };

export function loadPrefs(): ReminderPrefs {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

export function savePrefs(p: ReminderPrefs): void {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(p));
  } catch {
    /* приватный режим — настройка не переживёт перезапуск, и ладно */
  }
}

/**
 * Спрашивает разрешение на уведомления.
 *
 * Зовём не при запуске, а когда человек включает напоминание: системный запрос,
 * показанный без причины, чаще всего получает отказ — и второй раз его уже не
 * покажешь.
 */
export async function requestPermission(): Promise<boolean> {
  if (!isStoreBuild()) return false;
  try {
    const res = await LocalNotifications.requestPermissions();
    return res.display === 'granted';
  } catch {
    return false;
  }
}

export async function permissionGranted(): Promise<boolean> {
  if (!isStoreBuild()) return false;
  try {
    const res = await LocalNotifications.checkPermissions();
    return res.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Ежедневное напоминание об учёбе — главный пункт для проверки в магазине.
 *
 * Работает у любого человека, даже без записи на марафон и без расписания,
 * поэтому проверяющий гарантированно может его включить и убедиться, что
 * уведомления приходят. Заодно это честно полезная привычка.
 */
export async function scheduleDaily(time: string, title: string, body: string): Promise<void> {
  if (!isStoreBuild()) return;
  const [h, m] = time.split(':').map(Number);
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_ID }] });
    await LocalNotifications.schedule({
      notifications: [
        {
          id: DAILY_ID,
          title,
          body,
          schedule: { on: { hour: h, minute: m }, allowWhileIdle: true },
        },
      ],
    });
  } catch {
    /* нет разрешения — уведомление просто не появится */
  }
}

export async function cancelDaily(): Promise<void> {
  if (!isStoreBuild()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_ID }] });
  } catch {
    /* нечего отменять */
  }
}

type Upcoming = { id: string; at: Date; title: string; body: string };

/**
 * Перепланирует напоминания о занятиях и сроках сдачи.
 *
 * Вызывается при обновлении расписания. Сначала снимает прежние — иначе после
 * переноса занятия человек получил бы напоминание о том, чего уже нет.
 */
export async function rescheduleFromSchedule(lessons: Upcoming[], homework: Upcoming[]): Promise<void> {
  if (!isStoreBuild()) return;
  try {
    const pending = await LocalNotifications.getPending();
    const stale = pending.notifications.filter((n) => n.id >= LESSON_BASE);
    if (stale.length) await LocalNotifications.cancel({ notifications: stale.map((n) => ({ id: n.id })) });

    const now = Date.now();
    const items = [
      ...lessons.map((l, i) => ({ ...l, id: LESSON_BASE + i, at: new Date(l.at.getTime() - 60 * 60 * 1000) })),
      ...homework.map((h, i) => ({ ...h, id: HOMEWORK_BASE + i, at: new Date(h.at.getTime() - 24 * 60 * 60 * 1000) })),
    ]
      .filter((x) => x.at.getTime() > now) // прошедшее не планируем
      .sort((a, b) => a.at.getTime() - b.at.getTime())
      .slice(0, MAX_SCHEDULED); // скользящее окно — предел iOS

    if (!items.length) return;
    await LocalNotifications.schedule({
      notifications: items.map((x) => ({
        id: x.id,
        title: x.title,
        body: x.body,
        schedule: { at: x.at, allowWhileIdle: true },
      })),
    });
  } catch {
    /* нет разрешения либо плагин недоступен */
  }
}

/** Сколько напоминаний сейчас стоит — для настроек и для проверки. */
export async function pendingReminders(): Promise<number> {
  if (!isStoreBuild()) return 0;
  try {
    return (await LocalNotifications.getPending()).notifications.length;
  } catch {
    return 0;
  }
}
